import { useCustom, useCustomMutation } from '@refinedev/core';
import { Table, Tag, Button, Modal, message } from 'antd';
import { Ticket, Plus, Trash2 } from 'lucide-react';
import { useState, type FC } from 'react';

import CouponCreateModal from '@/components/ops/billing/CouponCreateModal';

interface CouponRecord {
  id: string;
  name: string;
  code: string;
  percentOff: number | null;
  amountOff: number | null;
  currency: string | null;
  duration: string;
  durationInMonths: number | null;
  maxRedemptions: number | null;
  timesRedeemed: number;
  valid: boolean;
  createdAt: string;
}

const fmtDate = (d: string | null) =>
  d ? new Date(d).toLocaleDateString('fr-FR') : '--';

const fmtDiscount = (r: CouponRecord) => {
  if (r.percentOff) return `${r.percentOff}%`;
  if (r.amountOff && r.currency) {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: r.currency,
    }).format(r.amountOff / 100);
  }
  return '--';
};

const DURATION_LABELS: Record<string, string> = {
  once: 'Une fois',
  repeating: 'Recurrent',
  forever: 'Permanent',
};

const CouponsPage: FC = () => {
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isLoading, refetch } = useCustom<{ data: { items: CouponRecord[]; total: number } }>({
    url: '/api/v1/admin/billing/coupons',
    method: 'get',
  });

  const { mutate: deleteCoupon } = useCustomMutation();

  const coupons = data?.data?.data?.items ?? [];

  const handleDelete = (coupon: CouponRecord) => {
    Modal.confirm({
      title: 'Supprimer ce coupon ?',
      content: `Le coupon "${coupon.name}" sera definitivement supprime.`,
      okText: 'Supprimer',
      okButtonProps: { danger: true },
      cancelText: 'Annuler',
      onOk: () =>
        new Promise<void>((resolve, reject) => {
          deleteCoupon(
            {
              url: `/api/v1/admin/billing/coupons/${coupon.id}`,
              method: 'delete',
              values: {},
            },
            {
              onSuccess: () => {
                void message.success('Coupon supprime');
                void refetch();
                resolve();
              },
              onError: () => {
                void message.error('Erreur lors de la suppression');
                reject();
              },
            },
          );
        }),
    });
  };

  return (
    <div className="space-y-6">
      <GradientHeader />

      <div className="flex items-center justify-end">
        <Button
          type="primary"
          icon={<Plus className="h-3.5 w-3.5" />}
          onClick={() => setCreateOpen(true)}
        >
          Creer coupon
        </Button>
      </div>

      <div className="fc-card overflow-hidden p-0">
        <Table
          dataSource={coupons}
          loading={isLoading}
          rowKey="id"
          size="middle"
          scroll={{ x: 900 }}
          pagination={{ pageSize: 15 }}
        >
          <Table.Column dataIndex="name" title="Nom" />
          <Table.Column
            dataIndex="code"
            title="Code"
            render={(code: string) => (
              <span className="font-mono text-xs text-slate-600 dark:text-slate-400">
                {code || '--'}
              </span>
            )}
          />
          <Table.Column
            title="Reduction"
            render={(_: unknown, r: CouponRecord) => (
              <span className="font-medium text-emerald-600 dark:text-emerald-400">
                -{fmtDiscount(r)}
              </span>
            )}
          />
          <Table.Column
            dataIndex="duration"
            title="Duree"
            render={(d: string, r: CouponRecord) => {
              let label = DURATION_LABELS[d] ?? d;
              if (d === 'repeating' && r.durationInMonths) {
                label += ` (${r.durationInMonths} mois)`;
              }
              return <span className="text-sm text-slate-600 dark:text-slate-300">{label}</span>;
            }}
          />
          <Table.Column
            title="Utilisations"
            render={(_: unknown, r: CouponRecord) => (
              <span className="text-sm text-slate-600 dark:text-slate-300">
                {r.timesRedeemed}
                {r.maxRedemptions ? ` / ${r.maxRedemptions}` : ''}
              </span>
            )}
          />
          <Table.Column
            dataIndex="valid"
            title="Actif"
            width={80}
            render={(valid: boolean) => (
              <Tag color={valid ? 'success' : 'default'}>
                {valid ? 'Actif' : 'Inactif'}
              </Tag>
            )}
          />
          <Table.Column
            dataIndex="createdAt"
            title="Cree le"
            width={110}
            render={(d: string) => (
              <span className="text-xs text-slate-500 dark:text-slate-400">{fmtDate(d)}</span>
            )}
          />
          <Table.Column
            title="Actions"
            width={80}
            render={(_: unknown, r: CouponRecord) => (
              <Button
                type="text"
                danger
                size="small"
                icon={<Trash2 className="h-3.5 w-3.5" />}
                onClick={() => handleDelete(r)}
              />
            )}
          />
        </Table>
      </div>

      <CouponCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={() => {
          setCreateOpen(false);
          void refetch();
        }}
      />
    </div>
  );
};

const GradientHeader: FC = () => (
  <div className="fc-gradient-header rounded-2xl p-6 text-white">
    <div className="flex items-center gap-3">
      <Ticket className="h-6 w-6" />
      <div>
        <h1 className="text-xl font-bold">Coupons</h1>
        <p className="mt-1 text-sm text-blue-100">
          Gestion des codes promotionnels Stripe
        </p>
      </div>
    </div>
  </div>
);

export default CouponsPage;
